import React, { useEffect, useState, useRef } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import { toast } from "react-toastify";
import Api from "../Config/ApiConfig";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Paper,
  Grid,
  CircularProgress,
  Tooltip,
  IconButton
} from "@mui/material";
import { 
  Refresh as RefreshIcon, 
  PowerSettingsNew as PowerIcon, 
  Logout as LogoutIcon, 
  PlayArrow as StartIcon,
  QrCode as QrIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon
} from "@mui/icons-material";

const WhatsAppSettings = () => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [session, setSession] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [isLoginFlow, setIsLoginFlow] = useState(false);
  const prevStatusRef = useRef(null);

  const fetchSettings = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await Api.get("/whatsapp-settings");
      if (response.data && response.data.success) {
        setEnabled(response.data.data.enabled);
      }
      await fetchSessionStatus();
    } catch (error) {
      console.error("Error fetching WhatsApp settings:", error);
      toast.error("Failed to load WhatsApp settings");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchSessionStatus = async () => {
    try {
      const response = await Api.get("/whatsapp-settings/session");
      if (response.data && response.data.success) {
        const currentSession = response.data.data;
        setSession(currentSession);
        
        // Auto-refresh logic: if status just became WORKING, and it wasn't before
        if (currentSession.status === "WORKING" && prevStatusRef.current !== "WORKING") {
          if (prevStatusRef.current) {
             toast.success("WhatsApp Connected Successfully!");
             setIsLoginFlow(false);
          }
        }
        
        prevStatusRef.current = currentSession.status;

        // If we need to scan QR, fetch screenshot
        if (currentSession.status === "SCAN_QR_CODE" || currentSession.status === "STARTING") {
          fetchScreenshot();
        } else {
          setScreenshot(null);
        }
      }
    } catch (error) {
      console.error("Error fetching session status:", error);
    }
  };

  const fetchScreenshot = async () => {
    try {
      const response = await Api.get("/whatsapp-settings/session/screenshot");
      if (response.data && response.data.success) {
        setScreenshot(response.data.data.screenshot);
      }
    } catch (error) {
      console.error("Error fetching screenshot:", error);
    }
  };

  const handleAction = async (action) => {
    try {
      setLoading(true);
      let response;
      let message = "";

      switch (action) {
        case 'start':
          response = await Api.post("/whatsapp-settings/session/start");
          message = "Starting WhatsApp engine...";
          break;
        case 'stop':
          if (!window.confirm("Stop the session? You will need to start it again to send messages.")) return;
          response = await Api.delete("/whatsapp-settings/session");
          message = "WhatsApp engine stopped.";
          break;
        case 'logout':
          if (!window.confirm("Are you sure you want to logout? This will unpair your phone.")) return;
          response = await Api.post("/whatsapp-settings/session/logout");
          message = "Logged out from WhatsApp.";
          break;
        case 'login':
          setIsLoginFlow(true);
          response = await Api.post("/whatsapp-settings/session/start");
          message = "Initializing login...";
          break;
        default: return;
      }

      if (response.data && response.data.success) {
        toast.info(message);
        setTimeout(() => {
          fetchSessionStatus();
          // If we stopped or logged out, backend auto-disabled notifications, so sync the UI
          if (action === 'stop' || action === 'logout') {
            fetchSettings(false);
          }
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Action ${action} failed`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnable = async () => {
    try {
      setLoading(true);
      const newEnabled = !enabled;
      const response = await Api.post("/whatsapp-settings", { enabled: newEnabled });
      if (response.data && response.data.success) {
        setEnabled(newEnabled);
        toast.success(`WhatsApp notifications ${newEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    const interval = setInterval(fetchSessionStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusDisplay = () => {
    const status = session?.status || "UNKNOWN";
    switch (status) {
      case "WORKING": 
        return { label: "Connected", color: "#10b981", icon: <CheckCircleIcon fontSize="small" /> };
      case "SCAN_QR_CODE": 
        return { label: "Scan QR", color: "#f59e0b", icon: <QrIcon fontSize="small" /> };
      case "STARTING": 
        return { label: "Starting...", color: "#3b82f6", icon: <CircularProgress size={16} color="inherit" /> };
      case "STOPPED": 
        return { label: "Stopped", color: "#ef4444", icon: <PowerIcon fontSize="small" /> };
      default: 
        return { label: status, color: "#6b7280", icon: <PendingIcon fontSize="small" /> };
    }
  };

  const statusInfo = getStatusDisplay();

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Communicating with WhatsApp...">
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                WhatsApp Center
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage global delivery and system connection
              </Typography>
            </Box>
            <IconButton onClick={() => fetchSettings(true)} sx={{ bgcolor: "white", boxShadow: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Box>

          <Grid container spacing={3}>
            {/* Delivery Switch Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 3, height: '100%', boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#f0fdf4", color: "#16a34a", mr: 2 }}>
                      <CheckCircleIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Delivery</Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Master switch for all automated WhatsApp notifications.
                  </Typography>

                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: enabled ? "#f0fdf4" : "#f8fafc",
                    border: `1px solid ${enabled ? "#dcfce7" : "#e2e8f0"}`,
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography sx={{ fontWeight: 600, color: enabled ? "#166534" : "#64748b" }}>
                        {enabled ? "Active" : "Paused"}
                      </Typography>
                      <Switch 
                        checked={enabled} 
                        onChange={handleToggleEnable}
                        color="success"
                        disabled={session?.status !== "WORKING"}
                      />
                    </Box>
                    {session?.status !== "WORKING" && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                        Connection required to enable delivery.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Connection Status Card */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#eff6ff", color: "#2563eb", mr: 2 }}>
                        <StartIcon />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>System Connection</Typography>
                    </Box>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 1, 
                      px: 2, 
                      py: 0.75, 
                      borderRadius: 10, 
                      bgcolor: `${statusInfo.color}15`,
                      color: statusInfo.color,
                      border: `1px solid ${statusInfo.color}30`
                    }}>
                      {statusInfo.icon}
                      <Typography variant="caption" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                        {statusInfo.label}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={screenshot ? 6 : 12}>
                      {session?.me && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block">CONNECTED AS</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>+{session.me.id.split('@')[0]}</Typography>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        {session?.status === "STOPPED" || session?.status === "UNKNOWN" ? (
                          <Button 
                            variant="contained" 
                            startIcon={<StartIcon />}
                            onClick={() => handleAction('login')}
                            sx={{ borderRadius: 2, textTransform: "none", py: 1, px: 3, bgcolor: "#3b82f6" }}
                          >
                            Login / Start
                          </Button>
                        ) : (
                          <>
                            {session?.status !== "WORKING" && (
                              <Button 
                                variant="contained" 
                                color="warning"
                                startIcon={<QrIcon />}
                                onClick={() => handleAction('login')}
                                sx={{ borderRadius: 2, textTransform: "none" }}
                              >
                                Show QR
                              </Button>
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Button 
                                variant="outlined" 
                                color="error"
                                startIcon={<LogoutIcon />}
                                onClick={() => handleAction('logout')}
                                sx={{ borderRadius: 2, textTransform: "none", width: "100%" }}
                              >
                                Logout
                              </Button>
                              <Typography variant="caption" color="text.secondary" align="center">
                                Unpairs your device
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Button 
                                variant="outlined" 
                                color="inherit"
                                startIcon={<PowerIcon />}
                                onClick={() => handleAction('stop')}
                                sx={{ borderRadius: 2, textTransform: "none", width: "100%" }}
                              >
                                Stop
                              </Button>
                              <Typography variant="caption" color="text.secondary" align="center">
                                Halts engine (stays paired)
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Grid>

                    {screenshot && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            {session?.status === "SCAN_QR_CODE" ? "SCAN TO LINK ACCOUNT" : "CURRENT ENGINE VIEW"}
                          </Typography>
                          <Paper elevation={0} sx={{ 
                            p: 1.5, 
                            border: "2px solid #e2e8f0", 
                            borderRadius: 3, 
                            display: "inline-block",
                            lineHeight: 0,
                            overflow: "hidden",
                            bgcolor: "white"
                          }}>
                            <img 
                              src={screenshot} 
                              alt="WhatsApp" 
                              style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }} 
                            />
                          </Paper>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", alignItems: "center", p: 2, bgcolor: "#fffbeb", borderRadius: 2, border: "1px solid #fef3c7" }}>
            <InfoIcon sx={{ color: "#d97706", mr: 1.5 }} fontSize="small" />
            <Typography variant="caption" color="#92400e">
              The <strong>default</strong> session name is required for all automated system triggers. Do not change session parameters.
            </Typography>
          </Box>
        </Box>
      </LoadingOverlay>
    </Layout>
  );
};

export default WhatsAppSettings;
